import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../../assets/css/User/Users.css';
import PermissionModal from '../../popmodel/PermissionModal';
import { Trash2, Pencil } from "lucide-react";
import { Spinner } from 'react-bootstrap';

const API_BASE_URL = process.env.REACT_APP_API_URL;

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [modalPermissions, setModalPermissions] = useState([]);
  const [modalUserName, setModalUserName] = useState('');
  const navigate = useNavigate();

  // Rebuild loggedInUser from your existing localStorage keys
  const loggedInUser = {
    _id: localStorage.getItem('userId'),
    email: localStorage.getItem('email'),
    role: JSON.parse(localStorage.getItem('role') || '{}'),
  };

  const userPermissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const canCreate = userPermissions.includes("create_User");
  const canEdit = userPermissions.includes("update_User");
  const canDelete = userPermissions.includes("delete_User");
  const canToggleStatus = userPermissions.includes("update_User");

  useEffect(() => {
    const fetchUsers = () => {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Unauthorized: No token found. Please log in.');
        navigate('/login');
        return;
      }

      setLoading(true);

      axios.get(`${API_BASE_URL}/api/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then(res => {
          const usersWithPermissions = res.data.map(user => ({
            ...user,
            role: {
              ...user.role,
              permissions: Array.isArray(user.role?.permissions) ? user.role.permissions : [],
            },
          }));
          setUsers(usersWithPermissions);
        })
        .catch(err => {
          if (err.response && err.response.status === 401) {
            alert('Session expired. Please log in again.');
            navigate('/login');
          }
        })
        .finally(() => {
          setLoading(false);
        });
    };

    fetchUsers();
  }, [navigate]);

  // console.log('Logged in user:', loggedInUser);

  const filteredByRole = users.filter(user => {
    if (!loggedInUser.role || !loggedInUser._id) return false;

    const roleName = loggedInUser.role.name?.toLowerCase();
    if (roleName === 'admin') {
      return true;
    }
    if (roleName === 'seller') {
      const buyerId = user.buyerId && (user.buyerId.$oid || user.buyerId);
      return buyerId === loggedInUser._id;
    }
    return false;
  });

  const filteredUsers = filteredByRole.filter(user => {
    if (!searchTerm) return true;
    const lowerSearch = searchTerm.toLowerCase();
    return (
      user.name.toLowerCase().includes(lowerSearch) ||
      user.email.toLowerCase().includes(lowerSearch) ||
      (user.role?.name?.toLowerCase().includes(lowerSearch)) ||
      (user.role?.permissions?.some(perm => perm.toLowerCase().includes(lowerSearch)) || false)
    );
  });

  const handleDelete = (userId) => {
    const token = localStorage.getItem('token');
    if (window.confirm('Are you sure you want to delete this user?')) {
      axios.delete(`${API_BASE_URL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => {
          alert(res.data.message);
          fetchUsers();
        })
        .catch(err => console.error('Error deleting user:', err));
    }
  };

  const fetchUsers = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Unauthorized: No token found. Please log in.');
      navigate('/login');
      return;
    }
    axios.get(`${API_BASE_URL}/api/users`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => {
        const usersWithPermissions = res.data.map(user => ({
          ...user,
          role: {
            ...user.role,
            permissions: Array.isArray(user.role?.permissions) ? user.role.permissions : [],
          },
        }));
        setUsers(usersWithPermissions);
      })
      .catch(err => {
        if (err.response && err.response.status === 401) {
          alert('Session expired. Please log in again.');
          navigate('/login');
        }
      });
  };

  const handleStatusChange = (userId, currentStatus) => {
    const token = localStorage.getItem('token');
    const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
    axios.patch(`${API_BASE_URL}/api/users/${userId}/status`, { status: newStatus }, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        alert(res.data.message);
        fetchUsers();
      })
      .catch(err => console.error('Error updating status:', err));
  };

  const handleEdit = (user) => {
    navigate('/add-user', { state: { user } });
  };

  const openPermissionsModal = (user) => {
    const permissionNames = user.role?.permissions || [];
    setModalPermissions(permissionNames);
    setModalUserName(user.name);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalPermissions([]);
    setModalUserName('');
  };

  if (loading)
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" /> Loading Users...
      </div>
    );

  return (
    <div className="users-page container-fluid py-4 px-3 animate-fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="page-title m-0">👥 Manage Users</h2>
        {canCreate ? (
          <Link
            to="/add-user"
            className="btn shadow-sm"
            style={{ color: 'white', backgroundColor: '#102234' }}
          >
            ➕ Add New User
          </Link>
        ) : (
          <button
            className="btn shadow-sm"
            style={{ color: 'white', backgroundColor: 'rgb(52 45 45)', cursor: 'not-allowed' }}
            disabled
            title="You don't have permission to add users"
          >
            ➕ Add New User
          </button>
        )}
      </div>

      <div className="mb-3">
        <input
          type="text"
          className="form-control shadow-sm"
          placeholder="🔍 Search by name, email, role, or permission..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-bordered table-hover mb-0">
              <thead className="table-header">
                <tr>
                  <th>No.</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Permissions</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user, index) => (
                    <tr key={user._id} className="table-row">
                      <td data-label="Id">{index + 1}</td>
                      <td data-label="Name">{user.name}</td>
                      <td data-label="Email">{user.email}</td>
                      <td data-label="Role">{user.role?.name || "N/A"}</td>
                      <td data-label="Permissions">
                        <button
                          className="btn btn-link p-0"
                          onClick={() => openPermissionsModal(user)}
                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                          title="Click to view all permissions"
                        >
                          Permissions ({user.role?.permissions?.length || 0})
                        </button>
                      </td>
                      <td data-label="Status">
                        <div className="form-check form-switch">
                          <input
                            className={`form-check-input ${user.status === 'Active' ? 'bg-success border-success' : 'bg-danger border-danger'}`}
                            type="checkbox"
                            role="switch"
                            checked={user.status === 'Active'}
                            onChange={() => canToggleStatus && handleStatusChange(user._id, user.status)}
                            title={canToggleStatus ? "Toggle Status" : "Permission Denied"}
                            disabled={!canToggleStatus}
                          />
                        </div>
                      </td>
                      <td data-label="Actions">
                        <button
                          className="btn btn-sm me-2 btn-outline-primary"
                          onClick={() => canEdit && handleEdit(user)}
                          disabled={!canEdit}
                          title={canEdit ? "Edit User" : "Permission Denied"}
                        >
                          <Pencil size={16} />
                        </button>

                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => canDelete && handleDelete(user._id)}
                          disabled={!canDelete}
                          title={canDelete ? "Delete User" : "Permission Denied"}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-3 text-muted">
                      No matching users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Permission Modal */}
      <PermissionModal
        show={showModal}
        onClose={closeModal}
        permissions={modalPermissions}
        userName={modalUserName}
      />
    </div>
  );
}

export default Users;
